import clsx from 'clsx';
import { Variant } from './mautic-form-types';

type FieldType = 'checkbox' | 'input' | 'radio' | 'select' | 'label';

// Variant to encoded color mapping (used for SVG icons in select)
const variantColors: Record<Variant, string> = {
    light: '%23000000',
    'outline-dark': '%23000000',
    dark: '%23F4F4F5',
    'outline-light': '%23F4F4F5',
};

export const getEncodedColor = (variant: Variant): string => variantColors[variant];

// Focus: derive from variant instead of manually adding to each
const focusStyles = {
    light: 'focus-visible:ring-white focus-visible:border-neutral-900',
    dark: 'focus-visible:ring-neutral-900 focus-visible:border-whiteTransparent-60',
} as const;

const variantFocusTheme: Record<Variant, keyof typeof focusStyles> = {
    dark: 'dark',
    'outline-light': 'dark',
    light: 'light',
    'outline-dark': 'light',
};

// Base: layout only (shared across all variants)
const base: Record<FieldType, string> = {
    checkbox: 'border-2 transition-all duration-200 ease-in-out appearance-none absolute left-0 w-s h-s rounded-s cursor-pointer after:absolute after:left-0.5 after:top-0.5 after:w-s after:h-s outline-none ring-2 ring-transparent',
    input: 'transition-all duration-200 ease-in-out rounded-s p-xs w-full border-2 outline-none ring-2 ring-transparent',
    radio: 'border-2 transition-all duration-200 ease-in-out appearance-none absolute left-0 w-s h-s rounded-full cursor-pointer outline-none ring-2 ring-transparent',
    select: 'transition-all duration-200 ease-in-out rounded-s p-xs w-full border-2 outline-none ring-2 ring-transparent',
    label: 'absolute top-xs left-xs transition-all duration-200 ease-in-out placeholder-shown:sr-only',
};

// Variants: colors/theming only
const variants: Record<Variant, Record<FieldType, string>> = {
    dark: {
        checkbox: 'bg-blackTransparent-20 border-blackTransparent-20 text-secondary-200 after:text-secondary-200',
        input: 'text-white bg-blackTransparent-20 border-blackTransparent-10 placeholder:text-whiteTransparent-60',
        radio: 'bg-blackTransparent-20 border-blackTransparent-20 text-secondary-200',
        select: 'bg-blackTransparent-20 border-blackTransparent-10 text-white [&:has(option:checked[value=""])]:text-whiteTransparent-70 hover:border-neutral-900',
        label: 'text-whiteTransparent-60 placeholder-shown:bg-black',
    },
    'outline-dark': {
        checkbox: 'bg-transparent border-blackTransparent-60',
        input: 'bg-transparent placeholder-shown:border-blackTransparent-50 placeholder:text-blackTransparent-50 text-neutral-900 border-blackTransparent-40',
        radio: 'border-blackTransparent-60',
        select: 'bg-transparent [&:has(option:checked[value=""])]:border-blackTransparent-60 [&:has(option:checked[value=""])]:text-blackTransparent-60 border-secondary-900 text-neutral-900 hover:border-neutral-900',
        label: 'text-blackTransparent-40 placeholder-shown:bg-white',
    },
    light: {
        checkbox: 'bg-whiteTransparent-80 border-whiteTransparent-80',
        input: 'bg-white text-neutral-900 border-white placeholder-shown:border-blackTransparent-10 placeholder:text-neutral-600',
        radio: 'bg-whiteTransparent-80 border-whiteTransparent-80',
        select: 'bg-white border-white text-black [&:has(option:checked[value=""])]:text-neutral-600',
        label: 'text-neutral-600 bg-white',
    },
    'outline-light': {
        checkbox: 'border bg-transparent border-whiteTransparent-60',
        input: 'bg-transparent placeholder-shown:border-whiteTransparent-60 text-white border-white placeholder:text-whiteTransparent-60',
        radio: 'border border-whiteTransparent-60',
        select: 'bg-transparent border-white text-white [&:has(option:checked[value=""])]:border-whiteTransparent-40',
        label: 'text-whiteTransparent-60 placeholder-shown:bg-black',
    },
};

export const getClasses = (type: FieldType, variant: Variant) => {
    const focus = type === 'label' ? '' : focusStyles[variantFocusTheme[variant]];
    return clsx(base[type], variants[variant][type], focus);
};

/**
 * Get the appropriate error classes based on form variant.
 *
 * The error border is kept on focus (focus-visible:!border-primary-700) so the
 * field still reads as invalid while focused. Because that overrides the normal
 * border-based focus cue, the focus indicator for error fields is an offset
 * outline applied via CSS off the form's data-surface attribute - see defaults.css.
 */
export const getErrorClasses = (variant: Variant): { input: string; label: string } => {
    const baseInput = '!border-primary-700 placeholder-shown:!border-primary-700 focus-visible:!border-primary-700';

    if (variant === 'light') {
        return {
            input: baseInput + ' !bg-errorPink placeholder:text-primary-700 [&:has(option:checked[value=""])]:!text-primary-700',
            label: '!bg-errorPink text-primary-700',
        };
    }

    return {
        input: baseInput,
        label: '',
    };
};
